import { FC, useContext } from 'react'
import {
    Accordion,
    AccordionContext,
    Button,
    Col,
    Form,
    Row,
    useAccordionButton,
} from 'react-bootstrap'
import ToolboxItem from './ToolboxItem'
import { constants } from '../utils/constants'
import { ControlsPanelProps } from '../PageBuilder.d'
import SelectField from '../../../../common/forms/fields/SelectField'
import TextAreaField from '../../../../common/forms/fields/TextAreaField'
import TextField from '../../../../common/forms/fields/TextField'
import slugify from 'react-slugify'
import ImageField from '../../../../common/forms/fields/ImageField'
import { useAppSelector } from '../../../../../store/hooks'
import {
    getDefaultPreviewTheme,
    removeSettingsValue,
    setSettingsValue,
} from '../../../../common/utility/helpers'

const CustomToggle = ({
    children,
    eventKey,
    callback,
}: {
    children: JSX.Element | string
    eventKey: any
    callback?: any
}) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const { activeEventKey } = useContext(AccordionContext)

    const decoratedOnClick = useAccordionButton(
        eventKey,
        () => callback && callback(eventKey)
    )

    const isCurrentEventKey =
        activeEventKey === eventKey || activeEventKey.includes(eventKey)

    return (
        <h2 className="accordion-header">
            <button
                type="button"
                aria-expanded={isCurrentEventKey}
                className={`accordion-button text-${
                    theme === 'dark' ? 'white' : 'primary'
                } h1 ${isCurrentEventKey ? '' : 'collapsed'}`}
                onClick={decoratedOnClick}
            >
                {children}
            </button>
        </h2>
    )
}

const ControlsPanel: FC<ControlsPanelProps> = ({
    selectedPage,
    setSelectedPage,
    setToolboxItem,
    availablePages,
    formikProps,
    headerSizes,
    page,
    setPage,
    allowRemoveImage,
    setAllowRemoveImage,
    previewSrc,
    setPreviewSrc,
    previewTheme,
    setPreviewTheme,
}) => {
    const { theme } = useAppSelector((state) => state.theme)

    const toolboxItems = [
        // { type: constants.toolsTypes.HEADER, w: 12, h: 3 },
        { type: constants.toolsTypes.PAGE_SECTION, w: 12, h: 3 },
    ]

    const handleDragStart = (item, e) => {
        setToolboxItem(item)
    }

    const defaultPreviewTheme = getDefaultPreviewTheme()

    const handleChangePreviewTheme = (e, type) => {
        setPreviewTheme((prevState) => {
            const pt = previewTheme
                ? {
                      ...prevState,
                      colors: {
                          ...previewTheme.colors,
                          [type]: {
                              ...previewTheme.colors[type],
                              hex: e.target.value,
                          },
                      },
                  }
                : null

            setSettingsValue('previewTheme', pt)
            return pt
        })
    }

    return (
        <>
            <Form.Group
                className="sfy-field mb-3"
                controlId="page-builder-select-page"
            >
                <Form.Select
                    value={selectedPage || ''}
                    onChange={(e) => {
                        setSelectedPage(e.target.value)
                    }}
                >
                    <option value="">--- New page ---</option>
                    {availablePages.map((choice: any) => (
                        <option key={choice.id} value={choice.id}>
                            {choice.name}
                        </option>
                    ))}
                </Form.Select>
                <span className="bottom-border"></span>
            </Form.Group>
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <CustomToggle eventKey="0">General</CustomToggle>
                    <Accordion.Body
                        style={{
                            background: theme === 'dark' ? '#222' : '#eee',
                        }}
                    >
                        <Row>
                            <Col xl={4}>
                                <Form.Group
                                    className="sfy-field mb-3"
                                    controlId="formPreviewThemeSecondaryColor"
                                >
                                    <Form.Label>Secondary</Form.Label>
                                    <Form.Control
                                        type="color"
                                        value={
                                            previewTheme
                                                ? previewTheme.colors.secondary
                                                      .hex
                                                : defaultPreviewTheme.colors
                                                      .secondary.hex
                                        }
                                        onChange={(e) =>
                                            handleChangePreviewTheme(
                                                e,
                                                'secondary'
                                            )
                                        }
                                        className="w-100"
                                    />
                                    <span className="bottom-border"></span>
                                </Form.Group>
                            </Col>
                            <Col xl={4}>
                                <Form.Group
                                    className="sfy-field mb-3"
                                    controlId="formPreviewThemePrimaryColor"
                                >
                                    <Form.Label>Primary</Form.Label>
                                    <Form.Control
                                        type="color"
                                        value={
                                            previewTheme
                                                ? previewTheme.colors.primary
                                                      .hex
                                                : defaultPreviewTheme.colors
                                                      .primary.hex
                                        }
                                        onChange={(e) =>
                                            handleChangePreviewTheme(
                                                e,
                                                'primary'
                                            )
                                        }
                                        className="w-100"
                                    />
                                    <span className="bottom-border"></span>
                                </Form.Group>
                            </Col>
                            <Col xl={4}>
                                <Form.Group
                                    className="sfy-field mb-3"
                                    controlId="formPreviewThemePrimaryColor"
                                >
                                    <Form.Label>Reset</Form.Label>
                                    <Button
                                        variant="outline-primary"
                                        className="w-100"
                                        size="sm"
                                        onClick={(e) => {
                                            removeSettingsValue('previewTheme')
                                            setPreviewTheme(null)
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Form.Group>
                            </Col>
                        </Row>
                        <TextField
                            id="formPageTitle"
                            name="page_title"
                            label="Page Title"
                            placeholder="Enter page title"
                            formikProps={formikProps}
                            onBlur={(e) => {
                                setPage((prevState) => {
                                    return {
                                        ...prevState,
                                        page_title: e.target.value,
                                        code: slugify(
                                            formikProps.values.code ||
                                                formikProps.values.page_title
                                        ),
                                    }
                                })
                            }}
                        />
                        <TextField
                            id="formPageUrl"
                            name="code"
                            label="Page Url"
                            placeholder="Enter page name"
                            formikProps={formikProps}
                            onBlur={(e) => {
                                formikProps.setFieldValue(
                                    'code',
                                    e.target.value?.toString().toLowerCase()
                                )
                            }}
                            required
                        />
                        <TextField
                            id="formPageInternalName"
                            name="name"
                            label="Page Internal Name"
                            placeholder="Enter page internal name"
                            formikProps={formikProps}
                            required
                        />
                        <SelectField
                            id="formPageHeadersHeaderSize"
                            name="header_size_id"
                            label="Header Size"
                            formikProps={formikProps}
                            choices={headerSizes}
                        />
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                    <CustomToggle eventKey="2">Header Image</CustomToggle>
                    <Accordion.Body
                        style={{
                            background: theme === 'dark' ? '#222' : '#eee',
                        }}
                    >
                        <ImageField
                            id="pageImageFile"
                            name="header_image"
                            label="Image"
                            formikProps={formikProps}
                            imageId={page.header_image_id}
                            alt={`${page.name} image`}
                            allowRemoveImage={allowRemoveImage}
                            setAllowRemoveImage={setAllowRemoveImage}
                            previewSrc={previewSrc}
                            setPreviewSrc={setPreviewSrc}
                            updateRecord={setPage}
                        />
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="3">
                    <CustomToggle eventKey="3">Page Sections</CustomToggle>
                    <Accordion.Body
                        style={{
                            background: theme === 'dark' ? '#222' : '#eee',
                        }}
                    >
                        {toolboxItems.map((item) => (
                            <ToolboxItem
                                key={item.type.code}
                                toolboxItem={item}
                                onDragStart={(e) => handleDragStart(item, e)}
                            />
                        ))}
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                    <CustomToggle eventKey="1">Meta Data</CustomToggle>
                    <Accordion.Body
                        style={{
                            background: theme === 'dark' ? '#222' : '#eee',
                        }}
                    >
                        <TextAreaField
                            id="formMetaDataDescription"
                            name="meta_description"
                            label="Description"
                            formikProps={formikProps}
                        />
                        <TextAreaField
                            id="formMetaDataKeywords"
                            name="meta_keywords"
                            label="Keywords"
                            formikProps={formikProps}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </>
    )
}

export default ControlsPanel
